import { api, API_BASE } from './apiClient';
import type {
  SportInfo,
  SportsEvent,
  SportsEventVariants,
  SportsPreflightResult,
  SportsProviderName,
} from '@/types/sports';

/**
 * DASH session init response from /api/sports-stream/:eventId/dash-init.
 * The frontend uses `manifestUrl` to point Shaka.Player.load() and
 * `clearKeys` to configure Shaka's DRM. `streamKey` is informational —
 * the segment proxy reads it from the URL path that Shaka constructs.
 */
export interface SportsDashInit {
  manifestType: 'dash';
  manifestUrl: string;
  clearKeys: Record<string, string>;
  provider: SportsProviderName;
  streamKey: string;
}

export interface ListEventsQuery {
  live?: boolean;
  sport?: string;
}

/**
 * Absolute URL — HLS.js receives this directly and resolves the rewritten
 * proxy segment URLs in the playlist body against it. Same pattern as the
 * movie/TV streamingApi.buildPlaylistUrl.
 */
export function buildSportsPlaylistUrl(
  eventId: string,
  source?: SportsProviderName,
  streamNo?: number,
  sourceId?: string,
): string {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (sourceId) params.set('sourceId', sourceId);
  if (streamNo !== undefined) params.set('streamNo', String(streamNo));
  const qs = params.toString();
  return `${API_BASE}/api/sports-stream/${encodeURIComponent(eventId)}/playlist.m3u8${qs ? `?${qs}` : ''}`;
}

export function buildPosterUrl(eventId: string): string {
  return `${API_BASE}/api/sports/poster/${encodeURIComponent(eventId)}`;
}

export const sportsApi = {
  listSports(): Promise<{ sports: SportInfo[] }> {
    return api<{ sports: SportInfo[] }>('/api/sports/sports', { auth: true });
  },

  listEvents(query: ListEventsQuery = {}): Promise<{ events: SportsEvent[] }> {
    const params = new URLSearchParams();
    if (query.live) params.set('live', '1');
    if (query.sport) params.set('sport', query.sport);
    const qs = params.toString();
    return api<{ events: SportsEvent[] }>(`/api/sports/events${qs ? `?${qs}` : ''}`, { auth: true });
  },

  getEvent(eventId: string): Promise<{ event: SportsEvent }> {
    return api<{ event: SportsEvent }>(`/api/sports/event/${encodeURIComponent(eventId)}`, {
      auth: true,
    });
  },

  /**
   * List the selectable playback variants for an event, grouped by provider —
   * populates the source menu's per-provider sub-dropdown. Lazily hit when the
   * user opens the menu (the backend fans out to streamed's /api/stream on
   * demand). Best-effort: a failure just means the menu falls back to the
   * provider-level rows already present in `event.sources`.
   */
  listSources(eventId: string): Promise<SportsEventVariants> {
    return api<SportsEventVariants>(
      `/api/sports-stream/${encodeURIComponent(eventId)}/sources`,
      { auth: true },
    );
  },

  /**
   * Opens a DASH session and returns the manifest URL + ClearKey config.
   * Frontend (useShakaPlayer) calls this once per playback start; the
   * returned manifestUrl is what Shaka.Player.load() consumes. Server-side
   * this opens a new session and registers it in the shared session cache
   * (so segment proxy fetches can look up upstream headers by streamKey).
   */
  dashInit(
    eventId: string,
    source: SportsProviderName,
    streamNo?: number,
  ): Promise<SportsDashInit> {
    const params = new URLSearchParams();
    params.set('source', source);
    if (streamNo !== undefined) params.set('streamNo', String(streamNo));
    return api<SportsDashInit>(
      `/api/sports-stream/${encodeURIComponent(eventId)}/dash-init?${params.toString()}`,
      { auth: true },
    );
  },

  preflight(eventId: string, source: SportsProviderName): Promise<SportsPreflightResult> {
    return api<SportsPreflightResult>(
      `/api/sports-stream/${encodeURIComponent(eventId)}/preflight?source=${encodeURIComponent(source)}`,
      { auth: true },
    );
  },

  /**
   * Warm the backend resolve for a sports stream. Same best-effort contract
   * as prefetchStream() — backend returns 204 even on provider failure so the
   * hover never surfaces an error to the user.
   */
  async prefetch(
    eventId: string,
    source?: SportsProviderName,
    signal?: AbortSignal,
  ): Promise<void> {
    const qs = source ? `?source=${encodeURIComponent(source)}` : '';
    try {
      await api<void>(
        `/api/sports-stream/${encodeURIComponent(eventId)}/prefetch${qs}`,
        { auth: true, signal },
      );
    } catch {
      /* swallow — hover prefetch must never surface as a UI error */
    }
  },
};
